import {
  PLGEventPayload,
  CustomGtmEvent,
} from '../../../common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { useGetLoggedInUser } from '../../../common/generic/hooks';
import { getOrganizationOfUser } from '@upright/common/iam/organizations/common';
import { AppcuesEventId } from '@upright/ui/integrations/appcues/types';
import { trackAppcuesEvent } from '../../../integrations/appcues/index';
import { usePushGtmEvent } from '../../../common/generic/components/google-tag-manager/use-push-gtm-event/index';
import { useMaybeCsrdCompanyContext } from '../../../common/upright/model/output/scores/csrd/csrd-company-double-materiality/context';

// Partial on purpose: not every PLG event has an Appcues counterpart — the site-location funnel
// events are GTM-only — so a lookup miss here is a legitimate "no Appcues event", not a gap.
const GTM_EVENT_TO_APPCUES_EVENT: Partial<Record<CustomGtmEvent, AppcuesEventId>> = {
  [CustomGtmEvent.PLG_ACCOUNT_CREATION_STARTED]: AppcuesEventId.plgAccountCreationStarted,
  [CustomGtmEvent.PLG_ACCOUNT_CREATION_COMPLETED]: AppcuesEventId.plgAccountCreationCompleted,
  [CustomGtmEvent.PLG_JOINED_ORGANISATION]: AppcuesEventId.plgJoinedOrganisation,
  [CustomGtmEvent.PLG_CONFIRM_COMPANY_VIEWED]: AppcuesEventId.plgConfirmCompanyViewed,
  [CustomGtmEvent.PLG_COMPANY_CONFIRMED_PRODUCT_FETCH_STARTED]: AppcuesEventId.plgCompanyConfirmedProductFetchStarted,
  [CustomGtmEvent.PLG_PRODUCTS_FETCHED_SUCCESSFULLY]: AppcuesEventId.plgProductsFetchedSuccessfully,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_VIEWED]: AppcuesEventId.plgProductModificationOnboardingViewed,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_DELETED]:
    AppcuesEventId.plgProductModificationOnboardingProductDeleted,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_ALL_PRODUCTS_DELETED]:
    AppcuesEventId.plgProductModificationOnboardingAllProductsDeleted,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_ADDED]:
    AppcuesEventId.plgProductModificationOnboardingProductAdded,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_EDITED]:
    AppcuesEventId.plgProductModificationOnboardingProductEdited,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_ONBOARDING_PRODUCT_SET_CONFIRMED]:
    AppcuesEventId.plgProductModificationOnboardingProductSetConfirmed,
  [CustomGtmEvent.PLG_ASSESSMENT_TAKES_LONG_EMAIL_SENT]: AppcuesEventId.plgAssessmentTakesLongEmailSent,
  [CustomGtmEvent.PLG_ASSESSMENT_COMPLETED_SUCCESFULLY]: AppcuesEventId.plgAssessmentCompletedSuccessfully,
  [CustomGtmEvent.PLG_HOME_DASHBOARD_ACCESS]: AppcuesEventId.plgHomeDashboardAccess,
  [CustomGtmEvent.PLG_HOME_VIDEO_STARTED]: AppcuesEventId.plgHomeVideoStarted,
  [CustomGtmEvent.PLG_HOME_VIDEO_COMPLETED]: AppcuesEventId.hasWatchedDmaFreemiumWelcomeVideo,
  [CustomGtmEvent.PLG_HOME_VIEW_RESULTS_CLICKED]: AppcuesEventId.plgHomeViewResultsClicked,
  [CustomGtmEvent.PLG_HOME_DOCUMENTATION_TILE_CLICKED]: AppcuesEventId.plgHomeDocumentationTileClicked,
  [CustomGtmEvent.PLG_HOME_NEWS_TILE_CLICKED]: AppcuesEventId.plgHomeNewsTileClicked,
  [CustomGtmEvent.PLG_MATERIALITY_MATRIX_VIEWED]: AppcuesEventId.plgMaterialityMatrixViewed,
  [CustomGtmEvent.PLG_MATERIALITY_MATRIX_IMAGE_DOWNLOADED]: AppcuesEventId.materialityMatrixImageDownloaded,
  [CustomGtmEvent.PLG_MATERIAL_TOPICS_VIEWED]: AppcuesEventId.plgMaterialTopicsViewed,
  [CustomGtmEvent.PLG_SMP_VIEWED]: AppcuesEventId.plgSmpViewed,
  [CustomGtmEvent.PLG_SMP_IMPACT_CARD_VIEWED]: AppcuesEventId.plgSmpImpactCardViewed,
  [CustomGtmEvent.PLG_SMP_IMPACT_CARD_IMPACT_CALCULATION_TOOLTIP_VIEWED]:
    AppcuesEventId.plgSmpImpactCardImpactCalculationTooltipViewed,
  [CustomGtmEvent.PLG_SMP_IMPACT_CARD_PRODUCT_CALCULATION_TOOLTIP_VIEWED]:
    AppcuesEventId.plgSmpImpactCardProductCalculationTooltipViewed,
  [CustomGtmEvent.PLG_SMP_FINANCIAL_MATERIALITY_TAB_CLICKED]: AppcuesEventId.plgSmpFinancialMaterialityTabClicked,
  [CustomGtmEvent.PLG_SMP_RISKOPPORTUNITY_CARD_VIEWED]: AppcuesEventId.plgSmpRiskOpportunityCardViewed,
  [CustomGtmEvent.PLG_QUANTITATIVE_OVERVIEW_VIEWED]: AppcuesEventId.plgQuantitativeOverviewViewed,
  [CustomGtmEvent.PLG_TAILORING_COMMIT_COPIED_RO]: AppcuesEventId.tailoringRiskopportunityCopyedAsNew,
  [CustomGtmEvent.PLG_TAILORING_COMMIT_ADD_RO]: AppcuesEventId.tailoringRiskopportunityAdded,
  [CustomGtmEvent.PLG_TAILORING_COMMIT_COPIED_IMPACT]: AppcuesEventId.tailoringImpactCopyedAsNew,
  [CustomGtmEvent.PLG_TAILORING_COMMIT_ADD_IMPACT]: AppcuesEventId.tailoringImpactAdded,
  [CustomGtmEvent.PLG_TAILORING_COMMIT_DISABLE_RO]: AppcuesEventId.tailoringRiskopportunityDisabled,
  [CustomGtmEvent.PLG_TAILORING_COMMIT_DISABLE_IMPACT]: AppcuesEventId.tailoringImpactDisabled,
  [CustomGtmEvent.PLG_TAILORING_COMMIT_EDIT_IRO]: AppcuesEventId.plgTailoringCommitEditIro,
  [CustomGtmEvent.PLG_TAILORING_OWN_INSIGHTS_ACCESSED]: AppcuesEventId.plgTailoringOwnInsightsAccessed,
  [CustomGtmEvent.PLG_TAILORING_ADJUSTED_IMPACT_THRESHOLD_COMMIT]: AppcuesEventId.tailoringImpactMaterialityThreshold,
  [CustomGtmEvent.PLG_TAILORING_ADJUSTED_FINANCIAL_THRESHOLD_COMMIT]:
    AppcuesEventId.tailoringFinancialMaterialityThreshold,
  [CustomGtmEvent.PLG_TAILORING_CHANGE_LOG_VIEWED]: AppcuesEventId.plgTailoringChangeLogViewed,
  [CustomGtmEvent.PLG_TAILORING_DELETE_ALL_CHANGES_COMMIT]: AppcuesEventId.plgTailoringDeleteAllChangesCommit,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_VIEWED]: AppcuesEventId.plgProductModificationDashboardViewed,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_DELETED]:
    AppcuesEventId.plgProductModificationDashboardProductDeleted,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_ALL_PRODUCTS_DELETED]:
    AppcuesEventId.plgProductModificationDashboardAllProductsDeleted,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_ADDED]:
    AppcuesEventId.plgProductModificationDashboardProductAdded,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_PRODUCT_EDITED]:
    AppcuesEventId.plgProductModificationDashboardProductEdited,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_ABANDONED_WITHOUT_RECOMPUTE]:
    AppcuesEventId.plgProductModificationDashboardAbandonedWithoutRecompute,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_STARTED]:
    AppcuesEventId.plgProductModificationDashboardRecomputationStarted,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_COMPLETED_SUCCESSFULLY]:
    AppcuesEventId.plgProductModificationDashboardRecomputationCompletedSuccessfully,
  [CustomGtmEvent.PLG_PRODUCT_MODIFICATION_DASHBOARD_RECOMPUTATION_FAILED]:
    AppcuesEventId.plgProductModificationDashboardRecomputationFailed,
  [CustomGtmEvent.PLG_SIDE_NAVIGATION_FINANCIAL_EFFECTS_EXPANDED]:
    AppcuesEventId.plgSideNavigationFinancialEffectsExpanded,
  [CustomGtmEvent.PLG_SIDE_NAVIGATION_GEOGRAPHY_TOOLTIP_VIEWED]: AppcuesEventId.plgSideNavigationGeographyTooltipViewed,
  [CustomGtmEvent.PLG_SIDE_NAVIGATION_DISCLOSURES_TOOLTIP_VIEWED]:
    AppcuesEventId.plgSideNavigationDisclosuresTooltipViewed,
  [CustomGtmEvent.PLG_SIDE_NAVIGATION_FINANCIAL_STATEMENTS_TOOLTIP_VIEWED]:
    AppcuesEventId.plgSideNavigationFinancialStatementsTooltipViewed,
};

/**
 * Push PLG related event to GTM and Appcues.
 * Separated from usePushGtmEvent to allow custom behaviour.
 * Extends payload with userId and organizationId. The user is identified by id rather than by
 * email address, to keep personal data out of Google Analytics.
 *
 * Current implementation is PLG specific, but it would be good consider making this generic.
 *
 * @returns
 */
export function useEventTracking() {
  const push = usePushGtmEvent();
  const user = useGetLoggedInUser();
  const org = user ? getOrganizationOfUser(user) : undefined;
  const shouldTrackPlgEvents = useMaybeCsrdCompanyContext()?.shouldTrackPlgAppcuesEvents;

  return (payload: PLGEventPayload) => {
    if (!shouldTrackPlgEvents) {
      return;
    }
    const extendedPayload: PLGEventPayload = {
      ...payload,
      data: { userId: user?.id, organizationId: org?.id, ...payload.data },
    };

    push(extendedPayload);

    const appcuesEvent =
      payload.event in GTM_EVENT_TO_APPCUES_EVENT ? GTM_EVENT_TO_APPCUES_EVENT[payload.event] : undefined;
    if (appcuesEvent) {
      trackAppcuesEvent(appcuesEvent); // TODO: Add payload
    }
  };
}
